'use client'

import { type DragEvent } from 'react'
import {
  Phone,
  Bot,
  Users,
  Voicemail,
  Volume2,
  Clock,
  PhoneOff,
  Plus,
  ChevronDown,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import type { NodeType } from '@/lib/pbx/flow/types'

interface NodeTypeConfig {
  type: NodeType
  label: string
  icon: typeof Phone
  description: string
  color: string
}

const nodeTypes: NodeTypeConfig[] = [
  {
    type: 'incoming_call',
    label: 'Incoming Call',
    icon: Phone,
    description: 'Entry point',
    color: 'border-green-500 bg-green-50 dark:bg-green-950/30',
  },
  {
    type: 'ai_agent',
    label: 'Voice Agent',
    icon: Bot,
    description: 'AI voice assistant',
    color: 'border-purple-500 bg-purple-50 dark:bg-purple-950/30',
  },
  {
    type: 'operator',
    label: 'Operator',
    icon: Users,
    description: 'Forward to operator',
    color: 'border-blue-500 bg-blue-50 dark:bg-blue-950/30',
  },
  {
    type: 'voicemail',
    label: 'Voicemail',
    icon: Voicemail,
    description: 'Record message',
    color: 'border-orange-500 bg-orange-50 dark:bg-orange-950/30',
  },
  {
    type: 'play_message',
    label: 'Play Message',
    icon: Volume2,
    description: 'TTS or audio',
    color: 'border-cyan-500 bg-cyan-50 dark:bg-cyan-950/30',
  },
  {
    type: 'time_check',
    label: 'Time Check',
    icon: Clock,
    description: 'Schedule routing',
    color: 'border-yellow-500 bg-yellow-50 dark:bg-yellow-950/30',
  },
  {
    type: 'hangup',
    label: 'Hangup',
    icon: PhoneOff,
    description: 'End call',
    color: 'border-red-500 bg-red-50 dark:bg-red-950/30',
  },
]

interface FlowSidebarProps {
  className?: string
  onAddNode: (type: NodeType) => void
}

export function FlowSidebar({ className, onAddNode }: FlowSidebarProps) {
  const onDragStart = (event: DragEvent<HTMLDivElement>, nodeType: string) => {
    event.dataTransfer.setData('application/reactflow', nodeType)
    event.dataTransfer.effectAllowed = 'move'
  }

  return (
    <>
      <div className="shrink-0 border-b bg-background p-3 md:hidden">
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="w-full justify-between">
              <span className="flex items-center gap-2"><Plus className="h-4 w-4" />Add block</span>
              <ChevronDown className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-[var(--radix-dropdown-menu-trigger-width)] max-w-[calc(100vw-1.5rem)]">
            {nodeTypes.map((node) => {
              const Icon = node.icon
              return (
                <DropdownMenuItem key={node.type} onSelect={() => onAddNode(node.type)} className="gap-3 py-3">
                  <Icon className="h-5 w-5 shrink-0" />
                  <span>{node.label}</span>
                </DropdownMenuItem>
              )
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    <div className={cn('hidden w-64 shrink-0 overflow-y-auto border-r bg-background p-4 md:block', className)}>
      <h3 className="font-semibold text-sm mb-4">Blocks</h3>
      <div className="space-y-2">
        {nodeTypes.map((node) => {
          const Icon = node.icon
          return (
            <div
              key={node.type}
              className={cn(
                'flex items-center gap-3 p-3 rounded-lg border cursor-grab transition-colors',
                'hover:shadow-sm active:cursor-grabbing',
                node.color
              )}
              draggable
              onDragStart={(e) => onDragStart(e, node.type)}
            >
              <Icon className="w-5 h-5 flex-shrink-0" />
              <div className="min-w-0">
                <p className="font-medium text-sm">{node.label}</p>
                <p className="text-xs text-muted-foreground truncate">
                  {node.description}
                </p>
              </div>
            </div>
          )
        })}
      </div>
    </div>
    </>
  )
}
